import { parseUTCDate } from "../../../utils/FPEUtils";

class MissingDataObserver {
  dispatcher;
  threshold;

  constructor(dispatcher, threshold, context={}) {
    this.dispatcher = dispatcher;
    this.threshold = threshold;
    this.context = context;
  }

  monitoring(dataId, dh, layer, options={}) {
    /*
    WRAP.Data#setAutoUpdateをhookしてsetAutoUpdate('xxx',false)を呼び出し時に欠測状態を解除する
    layer#setVisibleのhookでもいいが、本質的ではないのでそちらはoption(interactLayerVisible)にしている。
    METAR/TAFのように共通のdhで複数のコンテンツを監視するケースではlayer#setVisibleで対応する必要がある
    interactLayerVisibleを指定しない場合は次の注意が必要。
    layerが表示状態であればsetAutoUpdate(false)で欠測フラグを倒す処理は動かない（手動で自動更新を止める場合には欠測フラグを倒さない）
    */
    if (!options.interactLayerVisible) {
      const orgSetAutoUpdate = dh.setAutoUpdate;
      dh.setAutoUpdate = function () {
        const args = Array.prototype.slice.call(arguments);
        orgSetAutoUpdate.apply(dh, args);
        const _layer = typeof layer === 'function' ? layer() : layer;
        //visible:true autoUpdate:falseの場合は欠測フラグは倒さない
        if (!_layer.visible() && args.slice(-1)[0] === false) {
          this.dispatcher({type: 'SET_CONTENTS_MISSING', payload: {id:dataId, missing: {missing:false}}});
          if (options.callback) {
            options.callback(dataId, false);
          }
        }
      }.bind(this);
      /*
      dhAutoUpdateがfalseの状態でvisibleをfalseに切り替えるとsetAutoUpdate(false)が叩かれないので
      コンテンツクラスのvisible状態を管理する変数(this.context)を監視して、visibleがfalseに切り替えられたときにdhのauto_updateがfalseであれば
      欠測フラグを倒す。
      逆にvisibleがtrueに切り替えたときにdhのauto_updateがfalseであれば保持しているタイムリストを使って欠測判定を行う
       */
      if (this.context.constructor.name === 'Object') {
        let visible = this.context[dataId];
        Object.defineProperty(this.context, `${dataId}`, {
          get: () => {
            return visible;
          },
          set: newValue => {
            //自動更新がoffの状態でvisibleがfalseになったときは欠測フラグを倒す
            if (visible !== newValue && newValue === false && dh.source[source].conf.auto_update === false) {
              this.dispatcher({type: 'SET_CONTENTS_MISSING', payload: {id: dataId, missing: {missing: false}}});
              if (options.callback) {
                options.callback(dataId, false);
              }
            } else if (visible !== newValue && newValue === true && dh.source[source].conf.auto_update === false) {
              //自動更新がoffの状態でvisibleがtrueになったときは欠測判定を行う
              this._judge(dataId, source, dh.retained[source], options);
            }
            visible = newValue;
          }
        });
      } else {
        let visible = this.context.visible;
        Object.defineProperty(this.context, 'visible', {
          get: () => {
            return visible;
          },
          set: newValue => {
            //自動更新がoffの状態でvisibleがfalseになったときは欠測フラグを倒す
            if (visible !== newValue && newValue === false && dh.source[source].conf.auto_update === false) {
              this.dispatcher({type: 'SET_CONTENTS_MISSING', payload: {id: dataId, missing: {missing: false}}});
              if (options.callback) {
                options.callback(dataId, false);
              }
            } else if (visible !== newValue && newValue === true && dh.source[source].conf.auto_update === false && dh.retained[source]) {
              //自動更新がoffの状態でvisibleがtrueになったときは欠測判定を行う
              this._judge(dataId, source, dh.retained[source], options);
            }
            visible = newValue;
          }
        })
      }
    } else {
      //setVisibleをhook
      const orgSetVisible = layer.setVisible;
      layer.setVisible = function (visible) {
        const args = Array.prototype.slice.call(arguments);
        orgSetVisible.apply(layer, args);
        if (args.slice(-1)[0] === false) {
          this.dispatcher({type: 'SET_CONTENTS_MISSING', payload: {id: dataId, missing: {missing: false}}});
          if (options.callback) {
            options.callback(dataId, false);
          }
        }
      }.bind(this);
    }

    let source;
    //タイムリストの更新をハンドリングして欠測判定を行う
    if (dh && dh.conf.validtime && dh.conf.validtime.update_interval) {
      source = 'validtime';
      dh.updated('validtime', ref => {
        this._judge(dataId, source, ref, options);
      });
    } else if (dh && dh.conf.timelist && dh.conf.timelist.update_interval) {
      source = 'timelist';
      dh.updated('timelist', ref => {
        this._judge(dataId, source, ref, options);
      });
    } else if (dh && dh.conf.data && dh.conf.data.update_interval) {
      //dataの場合はタイムリスト生成は利用する側で行う
      if (typeof options.makeTimelist !== 'function') {
        throw new Error('makeTimelist option required.')
      }
      source = 'data';
      dh.updated('data', ref => {
        this._judge(dataId, source, ref, options);
      });
    }
  }

  _judge(dataId, source, ref, options) {
    if (source === 'data') {
      const timelist = options.makeTimelist(dataId, ref);
      const thresholdSec = this._thresholdSec(dataId);
      const missing = isMissingData(timelist, thresholdSec);
      this.dispatcher({type: 'SET_CONTENTS_MISSING', payload: {id:dataId, missing: {missing:missing, expired: thresholdSec}}});
      if (options.callback) {
        options.callback(dataId, missing);
      }
    } else {
      const thresholdSec = this._thresholdSec(dataId);
      let timeList;
      if (options.makeTimelist) {
        timeList = options.makeTimelist(dataId, ref);
      } else {
        timeList = ref.validtime;
      }
      const missing = isMissingData(timeList, thresholdSec);
      this.dispatcher({type: 'SET_CONTENTS_MISSING', payload: {id:dataId, missing: {missing:missing, expired: thresholdSec}}});
      if (options.callback) {
        options.callback(dataId, missing);
      }
    }
  }

  _thresholdSec(id) {
    const thresholdSec = this.threshold[id];
    return  typeof thresholdSec === 'function' ? thresholdSec() : thresholdSec;
  }
}

const isMissingData =(timeList, thresholdSec) => {
  if (!thresholdSec === undefined) {
    throw new Error('invalid missing threshold.');
  }
  if (!timeList || timeList.length === 0) return true;
  const now = Date.now();
  return !timeList.some(time => {
    //現在時刻から規定時間内のデータが存在するか
    return now - parseUTCDate(time).getTime() <= thresholdSec * 1000;
  });
}

export default MissingDataObserver;
